// ライブラリ（統合・フォルダ対応）：端末内の素材（取り込み/DL済み）と、連携時は Drive 上の未DL素材を
// 1ページに統合。フォルダ（入れ子・作成/移動/削除）で整理、検索時はフォルダ横断のフラット表示。
// 取り込み・字幕付け・DL・再生・削除をここで完結。
// ※フォルダは現状この端末内（localFolders）。Drive folders.json への双方向同期＋item folderId 伝播は段2。
import {
  downloadedHashes,
  removeLocalItem,
  gdriveCatalogItems,
  downloadGDriveItem,
  localItems,
  importLocalFile,
  attachSubtitleFile,
  saveLocalSegments,
  localFolders,
  createLocalFolder,
  renameLocalFolder,
  deleteLocalFolder,
  moveLocalFolder,
  moveLocalItem,
} from '../lib/library.js'
import {
  fetchCatalog as fetchGDriveCatalog,
  isConnected as gdriveConnected,
  connect as gdriveConnect,
} from '../lib/gdrive.js'
import { autoSegment } from '../lib/segment.js'
import { fmtDuration, fmtBytes } from '../lib/format.js'
import { bgSync, syncFolders } from '../lib/sync.js'
import { describeError } from '../lib/pwa.js'
import { icon } from '../lib/icons.js'
import { t } from '../lib/i18n.js'
import { reserveItemSlot, releaseItemSlot, commitItemSlot, isPurchased, acquiredCount, FREE_LIMIT, isItemLocked } from '../lib/entitlements.js'
import { showPaywall } from '../lib/paywall.js'
import { autoUploadAll, updateItemFolder, deleteFromDrive } from '../lib/drivepush.js'

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}
function kindIcon(kind) {
  return icon(kind === 'video' ? 'video' : 'audio')
}
// folderId が実在フォルダを指すか。指さない（孤児＝PC由来など）なら root 扱いにして見失わない。
function effFolderId(folderId, validIds) {
  return folderId && validIds.has(folderId) ? folderId : null
}
function folderPath(id, byId) {
  const out = []
  let cur = id
  const seen = new Set()
  while (cur && byId.has(cur) && !seen.has(cur)) {
    seen.add(cur)
    out.unshift(byId.get(cur))
    cur = byId.get(cur).parentId || null
  }
  return out
}
function withDescendants(id, folders) {
  const set = new Set([id])
  let added = true
  while (added) {
    added = false
    for (const f of folders) if (set.has(f.parentId) && !set.has(f.id)) { set.add(f.id); added = true }
  }
  return set
}

// 1項目分の行。downloaded=端末にある（再生可・…メニュー）／locked=ロック／未downloaded=DLボタン。
function rowEl(item) {
  const el = document.createElement('div')
  const { downloaded, locked } = item
  // 未DL（Drive上のみ）は薄色（notdl）。DL済み＝再生可 / ロック。
  el.className = 'item' + (downloaded && !locked ? ' playable' : '') + (downloaded && locked ? ' locked-row' : '') + (!downloaded && item.mediaFile ? ' notdl' : '')
  el.dataset.hash = item.hash
  const dur = item.duration ? fmtDuration(item.duration) : ''
  const seg = item.segmentCount ? `・${t('local.segments', { n: item.segmentCount })}` : ''
  const size = !downloaded && item.sizeBytes ? `・${fmtBytes(item.sizeBytes)}` : ''
  const subBadge = item.hasSubtitles ? `<span class="badge">${t('local.subtitle')}</span>` : ''
  let action
  if (!downloaded) {
    action = item.mediaFile
      ? `<button class="mini" data-act="dl">${t('library.download')}</button><button class="mini secondary ico-only" data-act="more" aria-label="${t('local.more')}">${icon('more')}</button>`
      : `<span class="muted" style="font-size:12px">${t('library.notUploaded')}</span>`
  } else if (locked) {
    action = `<span class="play-hint locked-ico" aria-hidden="true">${icon('lock')}</span><button class="mini secondary ico-only" data-act="more" aria-label="${t('local.more')}">${icon('more')}</button>`
  } else {
    action = `<span class="play-hint" aria-hidden="true">${icon('playCircle')}</span><button class="mini secondary ico-only" data-act="more" aria-label="${t('local.more')}">${icon('more')}</button>`
  }
  el.innerHTML = `
    <div class="item-ico">${kindIcon(item.kind)}</div>
    <div class="item-body">
      <div class="item-name">${escapeHtml(item.name)}</div>
      <div class="item-meta muted">${dur}${seg}${size} ${subBadge}</div>
      <div class="item-progress" hidden><div class="bar"><span></span></div><span class="pct"></span></div>
    </div>
    <div class="item-action">${action}</div>`
  return el
}

function folderRowEl(folder, count) {
  const el = document.createElement('div')
  el.className = 'item folder-row'
  el.dataset.folder = folder.id
  el.innerHTML = `
    <div class="item-ico">${icon('folder')}</div>
    <div class="item-body">
      <div class="item-name">${escapeHtml(folder.name || t('library.untitledFolder'))}</div>
      <div class="item-meta muted">${t('library.itemCount', { n: count })}</div>
    </div>
    <div class="item-action">
      <button class="mini secondary ico-only" data-act="folder-more" aria-label="${t('local.more')}">${icon('more')}</button>
      <span class="chev muted">${icon('chevron')}</span>
    </div>`
  return el
}

// ===== オーバーレイ（メニュー/入力/選択） =====
function itemMenu(item) {
  return new Promise((resolve) => {
    const ov = document.createElement('div')
    ov.className = 'paywall-overlay sheet-overlay'
    ov.innerHTML = `
      <div class="menu-sheet" role="menu">
        <div class="menu-head">${escapeHtml(item.name)}</div>
        <button class="menu-item" data-a="play" role="menuitem">${icon('playCircle')}<span>${t('local.menu.play')}</span></button>
        <button class="menu-item" data-a="subtitle" role="menuitem">${icon('subtitle')}<span>${t('local.menu.addSub')}</span><div class="menu-sub muted">${t('local.menu.addSubHint')}</div></button>
        <button class="menu-item" data-a="move" role="menuitem">${icon('folder')}<span>${t('local.move')}</span></button>
        <button class="menu-item danger" data-a="remove" role="menuitem">${icon('trash')}<span>${t('common.remove')}</span></button>
        <button class="btn secondary" data-a="cancel">${t('common.close')}</button>
      </div>`
    const close = (v) => { ov.remove(); resolve(v) }
    ov.addEventListener('click', (e) => {
      if (e.target === ov) return close(null)
      const b = e.target.closest('button[data-a]')
      if (!b) return
      close(b.dataset.a === 'cancel' ? null : b.dataset.a)
    })
    document.body.appendChild(ov)
  })
}
function folderMenu(folder) {
  return new Promise((resolve) => {
    const ov = document.createElement('div')
    ov.className = 'paywall-overlay sheet-overlay'
    ov.innerHTML = `
      <div class="menu-sheet" role="menu">
        <div class="menu-head">${escapeHtml(folder.name || t('library.untitledFolder'))}</div>
        <button class="menu-item" data-a="open" role="menuitem">${icon('folder')}<span>${t('local.open')}</span></button>
        <button class="menu-item" data-a="rename" role="menuitem">${icon('subtitle')}<span>${t('local.rename')}</span></button>
        <button class="menu-item" data-a="move" role="menuitem">${icon('chevron')}<span>${t('local.move')}</span></button>
        <button class="menu-item danger" data-a="delete" role="menuitem">${icon('trash')}<span>${t('local.deleteFolder')}</span></button>
        <button class="btn secondary" data-a="cancel">${t('common.close')}</button>
      </div>`
    const close = (v) => { ov.remove(); resolve(v) }
    ov.addEventListener('click', (e) => {
      if (e.target === ov) return close(null)
      const b = e.target.closest('button[data-a]')
      if (!b) return
      close(b.dataset.a === 'cancel' ? null : b.dataset.a)
    })
    document.body.appendChild(ov)
  })
}
function chooseSlot(fileName) {
  return new Promise((resolve) => {
    const ov = document.createElement('div')
    ov.className = 'paywall-overlay'
    ov.innerHTML = `
      <div class="paywall">
        <div class="paywall-title">${t('local.attachTitle', { name: escapeHtml(fileName) })}</div>
        <button class="btn" data-s="main">${t('local.asMain')}</button>
        <button class="btn secondary" data-s="sub">${t('local.asSub')}</button>
        <div class="paywall-note">${t('local.attachNote')}</div>
        <button class="btn secondary" data-s="cancel">${t('local.cancel')}</button>
      </div>`
    const close = (v) => { ov.remove(); resolve(v) }
    ov.addEventListener('click', (e) => {
      if (e.target === ov) return close(null)
      const b = e.target.closest('button[data-s]')
      if (!b) return
      close(b.dataset.s === 'cancel' ? null : b.dataset.s)
    })
    document.body.appendChild(ov)
  })
}
function promptText(title, initial = '') {
  return new Promise((resolve) => {
    const ov = document.createElement('div')
    ov.className = 'paywall-overlay'
    ov.innerHTML = `
      <div class="paywall">
        <div class="paywall-title">${escapeHtml(title)}</div>
        <input class="prompt-input" id="pin" type="text" value="${escapeHtml(initial)}">
        <button class="btn" data-s="ok">${t('common.ok')}</button>
        <button class="btn secondary" data-s="cancel">${t('local.cancel')}</button>
      </div>`
    const input = ov.querySelector('#pin')
    const close = (v) => { ov.remove(); resolve(v) }
    const ok = () => { const v = input.value.trim(); close(v || null) }
    ov.addEventListener('click', (e) => {
      if (e.target === ov) return close(null)
      const b = e.target.closest('button[data-s]')
      if (!b) return
      b.dataset.s === 'ok' ? ok() : close(null)
    })
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') ok() })
    document.body.appendChild(ov)
    setTimeout(() => input.focus(), 50)
  })
}
function folderPicker(folders, excludeIds, title) {
  return new Promise((resolve) => {
    const list = []
    const walk = (parentId, d) => {
      folders
        .filter((f) => (f.parentId || null) === parentId && !excludeIds.has(f.id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
        .forEach((f) => { list.push({ f, d }); walk(f.id, d + 1) })
    }
    walk(null, 0)
    const rows = list
      .map(({ f, d }) => `<button class="menu-item" data-fid="${f.id}" style="padding-left:${12 + d * 18}px">${icon('folder')}<span>${escapeHtml(f.name || t('library.untitledFolder'))}</span></button>`)
      .join('')
    const ov = document.createElement('div')
    ov.className = 'paywall-overlay sheet-overlay'
    ov.innerHTML = `
      <div class="menu-sheet" role="menu">
        <div class="menu-head">${escapeHtml(title)}</div>
        <button class="menu-item" data-fid="" role="menuitem">${icon('library')}<span>${t('local.moveRoot')}</span></button>
        ${rows}
        <button class="btn secondary" data-fid="__cancel__">${t('local.cancel')}</button>
      </div>`
    const close = (v) => { ov.remove(); resolve(v) }
    ov.addEventListener('click', (e) => {
      if (e.target === ov) return close(false)
      const b = e.target.closest('button[data-fid]')
      if (!b) return
      const fid = b.dataset.fid
      if (fid === '__cancel__') return close(false)
      close(fid === '' ? null : fid)
    })
    document.body.appendChild(ov)
  })
}

function setProgress(el, frac, label) {
  const box = el.querySelector('.item-progress')
  box.hidden = false
  box.querySelector('.bar > span').style.width = `${Math.round(frac * 100)}%`
  box.querySelector('.pct').textContent = label
  el.querySelector('.item-action').innerHTML = ''
}

export async function renderLibrary(root) {
  root.innerHTML = `
    <div class="head-row">
      <h1>${t('nav.library')}</h1>
      <div style="display:flex;gap:6px">
        <button class="mini ico-btn" id="lib-newfolder">${icon('folder')}<span>${t('local.newFolder')}</span></button>
        <button class="mini ico-btn" id="lib-import">${icon('plus')}<span>${t('library.import')}</span></button>
        <button class="mini" id="btn-refresh">${t('library.refresh')}</button>
      </div>
    </div>
    <div id="lib-conn" class="lib-conn"></div>
    <input type="file" id="lib-file" multiple style="position:absolute;width:1px;height:1px;opacity:0;pointer-events:none">
    <input type="file" id="sub-file" style="position:absolute;width:1px;height:1px;opacity:0;pointer-events:none">
    <input id="lib-search" class="w-search" type="search" placeholder="${t('library.searchPlaceholder')}" autocomplete="off">
    <div id="lib-crumbs" class="lib-crumbs"></div>
    <div id="lib-status" class="muted" style="font-size:13px;margin-bottom:6px"></div>
    <div id="lib-quota" class="muted" style="font-size:12px;margin-bottom:6px"></div>
    <div id="lib-list"></div>`

  const listEl = root.querySelector('#lib-list')
  const statusEl = root.querySelector('#lib-status')
  const quotaEl = root.querySelector('#lib-quota')
  const connEl = root.querySelector('#lib-conn')
  const crumbsEl = root.querySelector('#lib-crumbs')
  const searchEl = root.querySelector('#lib-search')
  const fileEl = root.querySelector('#lib-file')
  const subFileEl = root.querySelector('#sub-file')

  let items = []
  let folders = []
  let currentFolderId = null
  let subTargetHash = null

  async function updateQuota() {
    if (isPurchased()) { quotaEl.textContent = ''; return }
    const used = await acquiredCount()
    quotaEl.textContent = t('free.quota', { used: Math.min(used, FREE_LIMIT), limit: FREE_LIMIT })
  }

  function updateConn() {
    if (gdriveConnected()) {
      connEl.innerHTML = `<span class="conn-badge on">${t('sync.connected')}</span>`
    } else {
      connEl.innerHTML = `<span class="conn-badge off">${t('sync.notConnectedBadge')}</span> <button class="mini" id="lib-connect">${t('sync.connect')}</button>`
      const b = connEl.querySelector('#lib-connect')
      if (b) b.onclick = async () => {
        b.disabled = true
        try { await gdriveConnect(); updateConn(); autoUploadAll(); await load() } catch { b.disabled = false }
      }
    }
  }

  async function load() {
    statusEl.textContent = t('library.loading')
    let cat = null
    let driveErr = null
    if (gdriveConnected()) {
      try { await syncFolders() } catch { /* オフライン等は無視 */ } // folders.json と同期（LWW）
      try { cat = await fetchGDriveCatalog() } catch (e) { driveErr = e }
    }
    // tombstone 伝播：catalog で deleted の項目は端末からも消す（他端末での Drive 削除を反映）。
    if (cat) {
      for (const [h, e] of Object.entries(cat.items || {})) {
        if (e && e.deleted) { try { await removeLocalItem(h) } catch { /* noop */ } }
      }
    }
    folders = await localFolders()
    const dled = await downloadedHashes()
    const local = await localItems()
    const byHash = new Map()
    for (const m of local) {
      byHash.set(m.hash, {
        hash: m.hash, name: m.name, kind: m.kind, duration: m.duration || 0,
        hasSubtitles: !!m.hasSubtitles, segmentCount: m.segmentCount || 0,
        folderId: m.folderId || null, downloaded: true, locked: isItemLocked(m), _item: m,
      })
    }
    if (cat) {
      for (const e of gdriveCatalogItems(cat)) { // gdriveCatalogItems は deleted を除外済み
        if (dled.has(e.hash) || byHash.has(e.hash)) continue
        byHash.set(e.hash, {
          hash: e.hash, name: e.name, kind: e.kind, duration: e.duration || 0,
          hasSubtitles: !!e.hasSubtitles, folderId: e.folderId || null,
          downloaded: false, locked: false, mediaFile: e.mediaFile, bundleFile: e.bundleFile, sizeBytes: e.sizeBytes || 0, _entry: e,
        })
      }
    }
    items = [...byHash.values()].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
    if (currentFolderId && !folders.some((f) => f.id === currentFolderId)) currentFolderId = null
    if (driveErr) statusEl.innerHTML = `<span class="err-inline">${escapeHtml(driveErr.message || t('library.fetchFailed'))}</span>`
    else statusEl.textContent = t('library.countStatus', { n: items.length, d: items.filter((i) => i.downloaded).length })
    render()
  }

  function renderCrumbs() {
    const byId = new Map(folders.map((f) => [f.id, f]))
    crumbsEl.innerHTML = ''
    const seg = (label, fid) => {
      const s = document.createElement('span')
      s.className = 'crumb' + (fid === currentFolderId ? ' current' : '')
      s.textContent = label
      s.onclick = () => { currentFolderId = fid; render() }
      return s
    }
    crumbsEl.appendChild(seg(t('nav.library'), null))
    for (const f of folderPath(currentFolderId, byId)) {
      const sep = document.createElement('span')
      sep.className = 'crumb-sep'
      sep.innerHTML = icon('chevron')
      crumbsEl.append(sep, seg(f.name || t('library.untitledFolder'), f.id))
    }
  }

  function render() {
    const q = (searchEl.value || '').trim().toLowerCase()
    listEl.innerHTML = ''
    // 検索中はフォルダ横断のフラット表示（パンくず/フォルダ行なし）。
    if (q) {
      crumbsEl.innerHTML = ''
      const shown = items.filter((it) => (it.name || '').toLowerCase().includes(q))
      if (!shown.length) { listEl.innerHTML = `<p class="muted">${t('library.noMatch')}</p>`; return }
      for (const it of shown) listEl.appendChild(rowEl(it))
      return
    }
    renderCrumbs()
    const validIds = new Set(folders.map((f) => f.id))
    const subs = folders
      .filter((f) => (f.parentId || null) === currentFolderId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'))
    const childItems = items.filter((it) => effFolderId(it.folderId, validIds) === currentFolderId)
    for (const f of subs) {
      const count = items.filter((it) => effFolderId(it.folderId, validIds) === f.id).length
      listEl.appendChild(folderRowEl(f, count))
    }
    for (const it of childItems) listEl.appendChild(rowEl(it))
    if (!subs.length && !childItems.length) {
      listEl.innerHTML = `<p class="muted">${currentFolderId ? t('local.emptyFolder') : t('library.emptyAll')}</p>`
    }
  }

  async function autoUpload() {
    const r = await autoUploadAll((p) => {
      statusEl.textContent = t('local.uploadingPhase', { name: p.name, phase: t('local.uploadPhase.' + p.phase) })
    })
    if (r && r.ok && r.uploaded > 0) await load()
  }

  // ---- 新規フォルダ（現フォルダの下に） ----
  root.querySelector('#lib-newfolder').onclick = async () => {
    const name = await promptText(t('local.newFolderTitle'))
    if (!name) return
    await createLocalFolder(name, currentFolderId)
    await load()
  }

  // ---- 取り込み（現フォルダへ） ----
  root.querySelector('#lib-import').onclick = () => fileEl.click()
  fileEl.onchange = async () => {
    const files = [...(fileEl.files || [])]
    fileEl.value = ''
    if (!files.length) return
    const targetFolder = currentFolderId
    let ok = 0
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (!(await reserveItemSlot())) { showPaywall(); break }
      statusEl.textContent = t('local.importing', { name: f.name, i: i + 1, n: files.length })
      let added = false
      try {
        const hash = await importLocalFile(f)
        added = true
        if (targetFolder) await moveLocalItem(hash, targetFolder)
        await load()
        statusEl.textContent = t('local.segmenting', { name: f.name })
        const cues = await autoSegment(f)
        if (cues.length) { await saveLocalSegments(hash, cues); await load() }
        ok++
      } catch (e) {
        statusEl.innerHTML = `<span class="err-inline">${t('local.importFailed', { msg: describeError(e) })}</span>`
      } finally {
        if (added) commitItemSlot(); else releaseItemSlot()
      }
    }
    await updateQuota()
    if (ok) statusEl.textContent = t('local.imported', { n: ok })
    autoUpload()
  }

  // ---- 字幕ファイル → メイン/サブ ----
  subFileEl.onchange = async () => {
    const f = (subFileEl.files || [])[0]
    const hash = subTargetHash
    subFileEl.value = ''
    subTargetHash = null
    if (!f || !hash) return
    const slot = await chooseSlot(f.name)
    if (!slot) return
    try {
      const r = await attachSubtitleFile(hash, f, slot)
      if (!r.ok) { statusEl.innerHTML = `<span class="err-inline">${t('local.subParseFail')}</span>`; return }
      statusEl.textContent = t('local.attached', { n: r.count })
      await load()
    } catch {
      statusEl.innerHTML = `<span class="err-inline">${t('local.subParseFail')}</span>`
    }
  }

  const play = (hash) => { location.hash = `#/play/${encodeURIComponent(hash)}` }

  listEl.onclick = async (ev) => {
    // フォルダ行（移動 / メニュー）
    const folderRow = ev.target.closest('.folder-row')
    if (folderRow) {
      const fid = folderRow.dataset.folder
      const folder = folders.find((f) => f.id === fid)
      if (!folder) return
      if (ev.target.closest('button[data-act="folder-more"]')) {
        const act = await folderMenu(folder)
        if (act === 'open') { currentFolderId = fid; render() }
        else if (act === 'rename') {
          const name = await promptText(t('local.renameTitle'), folder.name || '')
          if (name) { await renameLocalFolder(fid, name); await load() }
        } else if (act === 'move') {
          const dest = await folderPicker(folders, withDescendants(fid, folders), t('local.moveTitle'))
          if (dest !== false) { await moveLocalFolder(fid, dest); await load() }
        } else if (act === 'delete') {
          if (await confirmRemove(t('local.deleteFolderConfirm', { name: folder.name || '' }))) { await deleteLocalFolder(fid); await load() }
        }
        return
      }
      currentFolderId = fid
      render()
      return
    }
    // 項目行
    const row = ev.target.closest('.item')
    if (!row) return
    const hash = row.dataset.hash
    const item = items.find((i) => i.hash === hash)
    if (!item) return
    const btn = ev.target.closest('button[data-act]')
    if (!btn) {
      if (item.downloaded && !item.locked) play(hash)
      else if (item.locked) showPaywall()
      return
    }
    if (btn.dataset.act === 'dl') {
      if (!(await reserveItemSlot())) { showPaywall(); return }
      let okdl = false
      try {
        await downloadGDriveItem(item._entry, (p) => {
          const frac = p.total ? p.loaded / p.total : 0
          const label = p.phase === 'media' ? `${fmtBytes(p.loaded)} / ${fmtBytes(p.total)}` : p.phase === 'bundle' ? t('library.fetchingSubs') : ''
          setProgress(row, p.phase === 'bundle' ? 0.02 : frac, label)
        })
        okdl = true
      } catch (e) {
        row.querySelector('.item-progress').hidden = true
        statusEl.innerHTML = `<span class="err-inline">${escapeHtml(t('library.dlFailed', { msg: describeError(e) }))}</span>`
      } finally {
        if (okdl) { commitItemSlot(); await updateQuota(); await load() } else releaseItemSlot()
      }
    } else if (btn.dataset.act === 'more') {
      // 未DL（Drive上のみ）項目の…＝Driveから削除のみ（端末にコピーは無い）。
      if (!item.downloaded) {
        if (await confirmRemove(t('library.removeDriveConfirm', { name: item.name }))) {
          statusEl.textContent = t('library.removingDrive')
          try { await deleteFromDrive(hash) } catch (e) {
            statusEl.innerHTML = `<span class="err-inline">${t('library.removeDriveFailed', { msg: describeError(e) })}</span>`
          }
          await load()
        }
        return
      }
      const act = await itemMenu(item)
      if (act === 'play') { if (item.locked) showPaywall(); else play(hash) }
      else if (act === 'subtitle') { subTargetHash = hash; subFileEl.click() }
      else if (act === 'move') {
        const dest = await folderPicker(folders, new Set(), t('local.moveTitle'))
        if (dest !== false) { await moveLocalItem(hash, dest); await updateItemFolder(hash, dest); await load() }
      } else if (act === 'remove') {
        const choice = await removeChoice(item)
        if (choice === 'local') { await removeLocalItem(hash); await load() }
        else if (choice === 'drive') {
          statusEl.textContent = t('library.removingDrive')
          try { await deleteFromDrive(hash) } catch (e) {
            statusEl.innerHTML = `<span class="err-inline">${t('library.removeDriveFailed', { msg: describeError(e) })}</span>`
          }
          await removeLocalItem(hash)
          await load()
        }
      }
    }
  }

  // 削除の2択：端末から（容量解放）／ Driveからも（全端末・破壊的）。未連携なら端末からのみ。
  function removeChoice(item) {
    return new Promise((resolve) => {
      const driveBtn = gdriveConnected()
        ? `<button class="btn danger" data-s="drive">${t('library.removeDrive')}</button><div class="paywall-note">${t('library.removeDriveDesc')}</div>`
        : ''
      const ov = document.createElement('div')
      ov.className = 'paywall-overlay'
      ov.innerHTML = `<div class="paywall">
          <div class="paywall-title">${t('library.removeTitle', { name: escapeHtml(item.name) })}</div>
          <button class="btn secondary" data-s="local">${t('library.removeLocal')}</button>
          <div class="paywall-note">${t('library.removeLocalDesc')}</div>
          ${driveBtn}
          <button class="btn secondary" data-s="cancel">${t('local.cancel')}</button>
        </div>`
      const close = (v) => { ov.remove(); resolve(v) }
      ov.addEventListener('click', (e) => {
        if (e.target === ov) return close(null)
        const b = e.target.closest('button[data-s]')
        if (!b) return
        close(b.dataset.s === 'cancel' ? null : b.dataset.s)
      })
      document.body.appendChild(ov)
    })
  }

  function confirmRemove(msg) {
    return new Promise((resolve) => {
      const ov = document.createElement('div')
      ov.className = 'paywall-overlay'
      ov.innerHTML = `<div class="paywall"><div class="paywall-desc">${escapeHtml(msg)}</div>` +
        `<button class="btn danger" data-s="ok">${t('common.remove')}</button>` +
        `<button class="btn secondary" data-s="cancel">${t('local.cancel')}</button></div>`
      const close = (v) => { ov.remove(); resolve(v) }
      ov.addEventListener('click', (e) => {
        if (e.target === ov) return close(false)
        const b = e.target.closest('button[data-s]')
        if (!b) return
        close(b.dataset.s === 'ok')
      })
      document.body.appendChild(ov)
    })
  }

  searchEl.oninput = () => render()
  root.querySelector('#btn-refresh').onclick = () => load()

  async function autoSync() {
    if (!gdriveConnected()) return
    const r = await bgSync()
    if (r && r.ok && r.state && (r.state.pulled > 0 || r.state.pushed > 0)) await load()
  }

  updateConn()
  await load()
  updateQuota()
  autoUpload()
  autoSync()
}
